// Điều phối phòng (/pos/dispatch): đơn đã thu tiền chờ vào phòng → đối soát công nợ → KTV đón khách (API POS V2: 1b, 5, 6)
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input, Modal, Select, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { AlertTriangle, CheckCircle2, DoorOpen, HandCoins, Loader2, ReceiptText, RefreshCw, ShieldCheck, WifiOff } from 'lucide-react';
import {
  dispatchPosOrder, getPosWaitingDispatch, verifyPosCheckin, type PosCheckinVerify, type PosWaitingDispatch,
} from 'api/PosV2';
import { PAYMENT_STATUS_LABEL, canApprove, currentStaffId, explainPosError, parseViDateTime, primaryServiceName } from './remote';
import { StatusPill, formatPhone, money, usePosAuth } from './utils';
import PosShell from './components/PosShell';
import RemoteOrderModal from './components/RemoteOrderModal';

const POLL_MS = 30_000;

const PAY_STATUS_CLS: Record<string, string> = {
  PAID: 'bg-green-50 text-green-700 border-green-200',
  PARTIALLY_PAID: 'bg-amber-50 text-amber-700 border-amber-200',
  UNPAID: 'bg-red-50 text-red-600 border-red-200',
};
const payPill = (s: string) => <StatusPill label={PAYMENT_STATUS_LABEL[s] || s} cls={PAY_STATUS_CLS[s] || 'border-slate-200 bg-slate-50 text-slate-600'} />;
const digits = (v: unknown) => String(v ?? '').replace(/\D/g, '');
const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').toLowerCase();

/** Danh sách chờ vào phòng, tự làm mới mỗi 30 giây khi tab đang mở */
function useWaitingDispatch() {
  const [items, setItems] = useState<PosWaitingDispatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastLoaded, setLastLoaded] = useState<Date | null>(null);
  const busy = useRef(false);

  const load = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setLoading(true);
    const res = await getPosWaitingDispatch();
    busy.current = false;
    setLoading(false);
    if (!res.ok) { setError(res.message || 'Không tải được danh sách'); return; }
    // Chỉ đơn có lịch hẹn mới vào phòng làm dịch vụ – API hiện trả cả đơn bán lẻ sản phẩm (khách lẻ) đã thu tiền
    setItems((res.data || []).filter(o => o.appointment_id));
    setError('');
    setLastLoaded(new Date());
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  return { items, loading, error, lastLoaded, reload: load };
}

export default function PosDispatch() {
  const { can, realRole, isDemo } = usePosAuth();
  const waiting = useWaitingDispatch();
  const [q, setQ] = useState('');
  const [branch, setBranch] = useState<string | undefined>();
  const [checking, setChecking] = useState<PosWaitingDispatch | null>(null);
  const [orderView, setOrderView] = useState<number | null>(null);

  const branches = useMemo(() => Array.from(new Set(waiting.items.map(i => i.branch_name).filter(Boolean) as string[])).sort(), [waiting.items]);
  const data = useMemo(() => {
    const nq = normalize(q.trim());
    const d = digits(q);
    return waiting.items.filter(o =>
      (!branch || o.branch_name === branch) &&
      (!nq || o.order_code.toLowerCase().includes(q.trim().toLowerCase()) || normalize(o.customer_name || '').includes(nq) ||
        (d.length >= 3 && digits(o.customer_phone).includes(d))));
  }, [waiting.items, q, branch]);
  const debtCount = waiting.items.filter(o => o.debt_amount > 0).length;

  const staffId = currentStaffId();
  // Bảo lãnh nợ: chỉ Quản lý / Admin đang đăng nhập (có gắn nhân viên) — chưa có API duyệt bằng PIN
  const guarantee = !isDemo && canApprove(realRole, 'manager')
    ? (staffId ? { ok: true as const, staffId } : { ok: false as const, reason: 'Tài khoản đang đăng nhập chưa gắn nhân viên nên chưa bảo lãnh được.' })
    : { ok: false as const, reason: 'Chỉ Quản lý / Admin (có gắn nhân viên) đăng nhập mới bảo lãnh được.' };

  const columns: ColumnsType<PosWaitingDispatch> = [
    {
      key: 'order', title: 'Mã đơn', width: 190,
      render: (_, o) => (
        <button onClick={() => setOrderView(o.order_id)} className="text-left font-mono text-[13px] font-semibold text-[#2c6b7f] hover:underline">{o.order_code}</button>
      ),
    },
    {
      key: 'customer', title: 'Khách hàng', width: 190,
      render: (_, o) => (
        <div>
          <div className="font-semibold text-slate-800">{o.customer_name || 'Khách lẻ'}</div>
          {o.customer_phone && <div className="text-xs text-slate-500">{formatPhone(digits(o.customer_phone))}</div>}
        </div>
      ),
    },
    {
      key: 'service', title: 'Dịch vụ', width: 260,
      render: (_, o) => (
        <div>
          <span className="line-clamp-2">{primaryServiceName(o.primary_service) || '—'}</span>
          {o.items_count > 1 && <span className="text-xs text-slate-500">+{o.items_count - 1} dịch vụ khác</span>}
        </div>
      ),
    },
    { key: 'staff', title: 'KTV', width: 160, render: (_, o) => (o.assigned_staffs?.length ? o.assigned_staffs.join(', ') : <span className="text-slate-400">Chưa phân</span>) },
    // total_amount của API 1b là tổng trước chiết khấu nên hiện số đã thu cho khỏi nhầm
    { key: 'paid', title: 'Đã thu', width: 120, align: 'right', render: (_, o) => money(o.paid_amount) },
    {
      key: 'debt', title: 'Còn nợ', width: 120, align: 'right',
      render: (_, o) => (o.debt_amount > 0 ? <b className="text-red-600">{money(o.debt_amount)}</b> : <span className="text-slate-400">0</span>),
    },
    { key: 'status', title: 'Thanh toán', width: 150, render: (_, o) => payPill(o.payment_status) },
    {
      key: 'paid_at', title: 'Thu lúc', width: 110,
      render: (_, o) => { const iso = parseViDateTime(o.paid_at_iso || o.paid_at); return iso ? dayjs(iso).format('HH:mm DD/MM') : '—'; },
    },
    {
      key: 'actions', title: '', width: 200, fixed: 'right',
      render: (_, o) => can('create') ? (
        <button onClick={() => setChecking(o)}
          className={`flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-sm font-semibold text-white ${o.debt_amount > 0 ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#357f96] hover:bg-[#2c6b7f]'}`}>
          <DoorOpen className="h-4 w-4" /> Kiểm tra & đón khách
        </button>
      ) : null,
    },
  ];

  return (
    <PosShell
      title="Điều phối phòng"
      subtitle="Khách đã thu tiền đang chờ vào phòng. Kiểm tra công nợ trước khi KTV đón khách; còn nợ thì thu đủ hoặc Quản lý bảo lãnh."
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input.Search allowClear placeholder="Mã đơn, tên khách, SĐT…" value={q} onChange={e => setQ(e.target.value)} className="w-72" />
        {branches.length > 1 && (
          <Select allowClear placeholder="Chi nhánh" className="w-48" value={branch} onChange={setBranch} options={branches.map(b => ({ value: b, label: b }))} />
        )}
        <button onClick={waiting.reload} className="flex h-8 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-600 hover:bg-slate-50">
          <RefreshCw className={`h-4 w-4 ${waiting.loading ? 'animate-spin' : ''}`} />
          {waiting.lastLoaded ? `Cập nhật ${dayjs(waiting.lastLoaded).format('HH:mm')}` : 'Tải lại'}
        </button>
        <span className="text-sm text-slate-500">
          {waiting.items.length} khách chờ vào phòng{debtCount > 0 && <> · <b className="text-amber-700">{debtCount} còn nợ</b></>}
        </span>
      </div>

      {waiting.error && (
        <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <span className="flex items-center gap-1.5"><WifiOff className="h-4 w-4 shrink-0" /> Không tải được danh sách: {waiting.error}</span>
          <button onClick={waiting.reload} className="shrink-0 font-semibold underline">Thử lại</button>
        </div>
      )}

      <Table<PosWaitingDispatch>
        rowKey="order_id" size="middle" className="custom-ant-table" columns={columns} dataSource={data}
        loading={waiting.loading && !waiting.items.length} scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        rowClassName={o => (o.debt_amount > 0 ? 'bg-amber-50/40' : '')}
        locale={{ emptyText: q || branch ? 'Không có khách phù hợp' : 'Chưa có khách nào chờ vào phòng' }}
      />

      <CheckinModal
        order={checking}
        guarantee={guarantee}
        onClose={() => setChecking(null)}
        onDispatched={() => { setChecking(null); waiting.reload(); }}
        onCollectDebt={id => setOrderView(id)}
        refreshKey={orderView}
      />
      <RemoteOrderModal open={orderView !== null} orderKey={orderView} onClose={() => setOrderView(null)} onPaid={waiting.reload} />
    </PosShell>
  );
}

interface CheckinProps {
  order: PosWaitingDispatch | null;
  guarantee: { ok: true; staffId: number } | { ok: false; reason: string };
  onClose: () => void;
  onDispatched: () => void;
  onCollectDebt: (orderId: number) => void;
  /** Đổi khi popup thu nợ đóng → kiểm tra lại công nợ */
  refreshKey: number | null;
}

/** Popup đối soát công nợ (API 5) và đón khách vào phòng (API 6) */
function CheckinModal({ order, guarantee, onClose, onDispatched, onCollectDebt, refreshKey }: CheckinProps) {
  const [verify, setVerify] = useState<PosCheckinVerify | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);

  const run = useCallback(async (orderId: number) => {
    setLoading(true);
    setError('');
    const res = await verifyPosCheckin({ order_id: orderId });
    setLoading(false);
    if (!res.ok || !res.data) { setVerify(null); setError(explainPosError(res, 'Không kiểm tra được công nợ')); return; }
    setVerify(res.data);
  }, []);

  useEffect(() => {
    setVerify(null);
    setNote('');
    if (order) run(order.order_id);
  }, [order, run]);

  // Popup thu nợ vừa đóng (refreshKey về null) → kiểm tra lại
  const prevKey = useRef(refreshKey);
  useEffect(() => {
    if (order && prevKey.current !== null && refreshKey === null) run(order.order_id);
    prevKey.current = refreshKey;
  }, [refreshKey, order, run]);

  const dispatch = async (withGuarantee: boolean) => {
    if (!order) return;
    setSending(true);
    const res = await dispatchPosOrder(order.order_id, withGuarantee && guarantee.ok
      ? { guaranteed_by: guarantee.staffId, guarantee_note: note.trim() }
      : {});
    setSending(false);
    if (!res.ok) {
      message.error(explainPosError(res, 'Không điều phối được'));
      // Công nợ có thể đã đổi so với lúc kiểm tra
      if (res.code === 'DEBT_GUARANTEE_REQUIRED') run(order.order_id);
      return;
    }
    message.success(`${order.customer_name || 'Khách'} đã được chuyển vào phòng dịch vụ${res.data?.is_guaranteed ? ' (có bảo lãnh nợ)' : ''}`);
    onDispatched();
  };

  const debt = verify?.debt_balance || 0;
  // Trả góp: server cho vào phòng theo nợ ĐẾN HẠN, không theo tổng nợ còn lại
  const isInstallment = verify?.payment_type === 'INSTALLMENT';
  const dueNow = verify?.due_amount ?? debt;
  const blocked = !!verify && !verify.allow_service;
  const noteOk = note.trim().length >= 5;

  return (
    <Modal open={!!order} onCancel={() => !sending && onClose()} footer={null} centered width={600} destroyOnHidden
      title={order ? <span className="flex items-center gap-2"><DoorOpen className="h-5 w-5 text-[#2c6b7f]" /> Kiểm tra trước khi vào phòng · <span className="font-mono">{order.order_code}</span></span> : ''}>
      {order && (
        <div className="space-y-4">
          <div className="text-sm text-slate-600">
            <b className="text-slate-800">{order.customer_name || 'Khách lẻ'}</b>
            {order.customer_phone && <> · {formatPhone(digits(order.customer_phone))}</>}
            {order.branch_name && <> · {order.branch_name}</>}
          </div>

          {loading && <div className="flex items-center justify-center gap-2 py-8 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Đang đối soát công nợ…</div>}
          {error && !loading && (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <span>{error}</span>
              <button onClick={() => run(order.order_id)} className="shrink-0 font-semibold underline">Thử lại</button>
            </div>
          )}

          {verify && !loading && (
            <>
              <div className={`flex items-start gap-3 rounded-xl border p-4 ${blocked ? 'border-red-300 bg-red-50' : 'border-green-200 bg-green-50'}`}>
                {blocked ? <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-red-600" /> : <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-green-600" />}
                <div className="min-w-0">
                  <p className={`font-semibold ${blocked ? 'text-red-700' : 'text-green-700'}`}>{verify.message}</p>
                  <div className={`mt-1 grid gap-2 text-sm ${isInstallment ? 'grid-cols-4' : 'grid-cols-3'}`}>
                    {/* total_amount của API là tổng trước chiết khấu → tổng phải trả = đã thu + còn nợ */}
                    <div><div className="text-xs text-slate-500">Phải trả</div><b>{money(verify.paid_amount + debt)}</b></div>
                    <div><div className="text-xs text-slate-500">Đã thu</div><b>{money(verify.paid_amount)}</b></div>
                    <div><div className="text-xs text-slate-500">{isInstallment ? 'Còn nợ (trả góp)' : 'Còn nợ'}</div><b className={!isInstallment && debt > 0 ? 'text-red-600' : ''}>{money(debt)}</b></div>
                    {isInstallment && <div><div className="text-xs text-slate-500">Đến hạn</div><b className={dueNow > 0 ? 'text-red-600' : 'text-green-700'}>{money(dueNow)}</b></div>}
                  </div>
                  {isInstallment && !!verify.overdue_installments?.length && (
                    <ul className="mt-2 space-y-0.5 text-xs text-red-700">
                      {verify.overdue_installments.map(o => (
                        <li key={o.no}>Đợt {o.no} · hạn {o.due_date.split('-').reverse().join('/')} · còn {money(o.remaining)}đ · quá {o.overdue_days} ngày</li>
                      ))}
                    </ul>
                  )}
                  {isInstallment && !verify.overdue_installments?.length && verify.next_installment && (
                    <p className="mt-2 text-xs text-slate-600">
                      Đợt kế tiếp: đợt {verify.next_installment.no} · hạn {verify.next_installment.due_date.split('-').reverse().join('/')} · {money(verify.next_installment.remaining)}đ
                    </p>
                  )}
                </div>
              </div>

              {verify.items?.length > 0 && (
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm">
                  {verify.items.map((it, i) => (
                    <li key={i} className="flex justify-between gap-3 px-3 py-2">
                      <span className="min-w-0 text-slate-800">{it.service_name}{it.quantity > 1 ? ` ×${it.quantity}` : ''}</span>
                      <span className="shrink-0 text-xs text-slate-500">{it.staff_name ? `KTV: ${it.staff_name}` : 'Chưa phân KTV'}</span>
                    </li>
                  ))}
                </ul>
              )}

              {blocked ? (
                <div className="space-y-3">
                  <button onClick={() => onCollectDebt(order.order_id)}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#357f96] text-sm font-bold text-white hover:bg-[#2c6b7f]">
                    <HandCoins className="h-5 w-5" /> Thu nợ {money(debt)}đ
                  </button>
                  <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                    <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-amber-800"><ShieldCheck className="h-4 w-4" /> Quản lý bảo lãnh cho vào phòng khi còn nợ</div>
                    {guarantee.ok ? (
                      <>
                        <Input.TextArea rows={2} maxLength={300} value={note} onChange={e => setNote(e.target.value)}
                          placeholder="Lý do bảo lãnh (bắt buộc, tối thiểu 5 ký tự) – VD: Khách quen, làm xong thanh toán nốt" />
                        <button onClick={() => dispatch(true)} disabled={!noteOk || sending}
                          className="mt-2 flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-amber-400 bg-white text-sm font-bold text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50">
                          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Bảo lãnh & đón khách
                        </button>
                      </>
                    ) : <p className="text-xs text-amber-800">{guarantee.reason}</p>}
                  </div>
                </div>
              ) : (
                <button onClick={() => dispatch(false)} disabled={sending}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-green-600 text-sm font-bold text-white hover:bg-green-700 disabled:opacity-60">
                  {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <DoorOpen className="h-5 w-5" />} Đón khách vào phòng
                </button>
              )}
            </>
          )}

          <button onClick={() => onCollectDebt(order.order_id)} className="flex items-center gap-1 text-xs font-semibold text-[#2c6b7f] hover:underline">
            <ReceiptText className="h-3.5 w-3.5" /> Xem chi tiết đơn
          </button>
        </div>
      )}
    </Modal>
  );
}
